// services/school-ui/src/pages/promotion/QueueList.tsx
// Left-rail dense list for the promotion queue. One-line rows, sticky
// filter tabs, accent-ring on selected. Click a row to select; the
// page wires the selection into the right-pane PromotionDetail.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEligibleTeachers } from "../../api/school";
import type { FilterValue, PromotionRequest, Status } from "./types";

/** Build a user_id → display name lookup from /api/admin/teachers/eligible.
 *  Falls back to the email when the display name is empty (e.g. accounts
 *  created via the API workaround we used during the e2e walkthrough). */
function useUserNameById(): Map<string, string> {
  const teachers = useEligibleTeachers();
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of teachers.data ?? []) {
      m.set(tt.user_id, tt.display_name || tt.email);
    }
    return m;
  }, [teachers.data]);
}

/** Render either the resolved name or, when the lookup misses, the first
 *  8 chars of the UUID followed by an ellipsis. Slug-length UUIDs in the
 *  UI add noise; partial hex is a recognisable fallback that doesn't
 *  pretend to be an identity. */
export function renderRequester(
  userId: string | null,
  byId: Map<string, string>,
): string {
  if (!userId) return "(deleted)";
  const name = byId.get(userId);
  if (name) return name;
  return `${userId.slice(0, 8)}…`;
}

interface Props {
  requests: PromotionRequest[];
  selectedRequestId: string | null;
  onSelect: (id: string) => void;
  filter: FilterValue;
  onFilterChange: (v: FilterValue) => void;
  onRefresh: () => void;
}

const FILTERS: FilterValue[] = ["pending", "approved", "rejected", "withdrawn", "all"];

export function QueueList({
  requests,
  selectedRequestId,
  onSelect,
  filter,
  onFilterChange,
  onRefresh,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const userNameById = useUserNameById();
  return (
    <div className="flex max-h-[45vh] w-full shrink-0 flex-col border-b border-slate-200 md:h-full md:max-h-none md:w-[360px] md:border-b-0 md:border-r">
      <div className="flex items-center gap-1 border-b border-slate-200 px-3 py-2">
        {FILTERS.map((v) => (
          <button
            key={v}
            onClick={() => onFilterChange(v)}
            className={`text-xs px-2 py-1 rounded ${
              filter === v
                ? "bg-blue-50 ring-1 ring-blue-200 text-blue-600"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            {t(`promotion.filter.${v}`)}
          </button>
        ))}
        <button
          onClick={onRefresh}
          className="ml-auto text-xs px-2 py-1 rounded text-slate-500 hover:bg-slate-100"
          title={t("promotion.filter.refresh")}
          aria-label={t("promotion.filter.refresh")}
        >
          ↻
        </button>
      </div>
      <ul className="flex-1 overflow-y-auto">
        {requests.length === 0 ? (
          <li className="px-4 py-3 text-xs italic text-slate-500">
            {t("promotion.queue.empty")}
          </li>
        ) : (
          requests.map((r) => (
            <QueueRow
              key={r.id}
              request={r}
              selected={r.id === selectedRequestId}
              onClick={() => onSelect(r.id)}
              userNameById={userNameById}
            />
          ))
        )}
      </ul>
    </div>
  );
}

function QueueRow({
  request,
  selected,
  onClick,
  userNameById,
}: {
  request: PromotionRequest;
  selected: boolean;
  onClick: () => void;
  userNameById: Map<string, string>;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <li>
      <button
        onClick={onClick}
        className={`flex w-full items-center gap-2 border-b border-slate-200 px-3 py-2 text-left text-xs hover:bg-slate-100 ${
          selected ? "ring-1 ring-inset ring-blue-300 bg-blue-50" : ""
        }`}
      >
        <span className="truncate text-slate-700">
          {renderRequester(request.requested_by, userNameById)}
        </span>
        <span className="ml-auto shrink-0">
          <StatusPill status={request.status} />
        </span>
      </button>
      <div className="px-3 pb-2 text-[10px] text-slate-500">
        <code className="text-blue-500">{request.source_collection}</code>{" "}
        →{" "}
        <code className="text-blue-500">{request.target_collection}</code>{" "}
        · {relativeAge(request.requested_at, t)}
      </div>
    </li>
  );
}

function StatusPill({ status }: { status: Status }): JSX.Element {
  const styles: Record<Status, string> = {
    pending: "bg-blue-50 text-blue-600",
    approved: "bg-green-500/10 text-green-400",
    rejected: "bg-red-50 text-red-600",
    withdrawn: "bg-slate-100 text-slate-500",
  };
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wide ${styles[status]}`}
    >
      {status}
    </span>
  );
}

function relativeAge(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return t("promotion.queue.age.justNow");
  if (min < 60) return t("promotion.queue.age.minutes", { n: min });
  const h = Math.round(min / 60);
  if (h < 24) return t("promotion.queue.age.hours", { n: h });
  return t("promotion.queue.age.days", { n: Math.round(h / 24) });
}
